import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { 
  Plus, Edit, Trash2, CheckCircle, XCircle,
  AlertCircle, User, MapPin, TrendingUp, CreditCard, DollarSign, Clock, Loader2, Wifi, WifiOff, Bell
} from "lucide-react";
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';
import { ResponsiveMetricsGrid } from '@/components/ui/responsive-charts';
import { ResponsiveTable, ColumnDef } from '@/components/ui/responsive-table';
import { useTableColumns } from '@/hooks/useTableColumns';
import reservaService, { Reserva, ReservaEstatisticas } from '@/services/reservaService';
import { CreditoService } from '@/services/creditoService';
import { clientService, Client } from '@/services/clientService';
import { toTitleCase } from "@/utils/formatters";
import { useAdminSocket, ReservationStatusChangedPayload, StatsUpdatePayload, SystemUpdatePayload, SocketErrorPayload } from '@/hooks/useSocket';
import { useAuth } from '@/contexts/AuthContext';
import { AuthService } from '@/services/authService';
import { toast } from 'sonner';

// Removendo interface duplicada, usando a do service

// Tipo para estatísticas de conexão do socket
interface ConnectionStats {
  connected_users: number;
  total_connections: number;
  users_online: string[];
}

const AdminReservations = () => {
  const { user } = useAuth();
  const [reservations, setReservations] = useState<Reserva[]>([]);
  const [statistics, setStatistics] = useState<ReservaEstatisticas | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedStatus, setSelectedStatus] = useState<string>("TODOS");
  const [selectedClient, setSelectedClient] = useState<string>("TODOS");
  const [clients, setClients] = useState<Client[]>([]);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reserva | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Estados para modais de confirmação
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUsadoDialog, setShowUsadoDialog] = useState(false);
  const [reservaToCancel, setReservaToCancel] = useState<string | null>(null);
  const [reservaToMarkUsado, setReservaToMarkUsado] = useState<Reserva | null>(null);

  // 🆕 SOCKET.IO PARA ATUALIZAÇÕES EM TEMPO REAL
  const {
    isConnected,
    connectionError,
    reservationUpdates,
    systemAlerts,
    connectionStats,
    clearAlerts,
    clearUpdates
  } = useAdminSocket(user?.id, AuthService.getToken() || undefined);

  const loadReservations = React.useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        page: currentPage,
        limit: 10,
        ...(selectedStatus !== "TODOS" && { status: selectedStatus }),
        ...(selectedClient !== "TODOS" && { user_id: selectedClient })
      };
      
      const response = await reservaService.findAll(filters);
      setReservations(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      toast.error('Erro ao carregar reservas');
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedStatus, selectedClient]);

  const loadStatistics = React.useCallback(async () => {
    try {
      const stats = await reservaService.getEstatisticas();
      setStatistics(stats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  }, []);

  const loadClients = React.useCallback(async () => {
    try {
      const pageSize = 200;
      let page = 1;
      const allClients: Client[] = [];
      let hasNext = true;

      while (hasNext) {
        const response = await clientService.listClients({ page, limit: pageSize });
        allClients.push(...response.data);
        hasNext = response.pagination.hasNext;
        page += 1;
      }

      // Formatar e ordenar clientes (Ordem alfabética e Title Case)
      const formattedClients = allClients.map(client => ({
        ...client,
        nome_completo: toTitleCase(client.nome_completo)
      })).sort((a, b) => a.nome_completo.localeCompare(b.nome_completo, 'pt-BR'));

      setClients(formattedClients);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  }, []);

  // Carregar dados das reservas
  // Atualiza reservas e estatísticas quando filtros mudam
  useEffect(() => {
    loadReservations();
    loadStatistics();
    loadClients();
  }, [loadReservations, loadStatistics, loadClients]);

  // 🆕 PROCESSAR ATUALIZAÇÕES EM TEMPO REAL
  useEffect(() => {
    const updates = reservationUpdates as ReservationStatusChangedPayload[];
    if (updates.length > 0) {
      const latestUpdate = updates[0];
      
      // Atualizar a reserva na lista local
      setReservations(prev => 
        prev.map(reserva => 
          reserva.id === latestUpdate.reserva_id 
            ? { ...reserva, status: latestUpdate.data.status_novo as Reserva['status'] }
            : reserva
        )
      );

      // Mostrar notificação
      const statusText = latestUpdate.data.status_novo === 'EM_USO' ? 'Em Uso' : 
                        latestUpdate.data.status_novo === 'USADO' ? 'Usado' : 
                        latestUpdate.data.status_novo === 'EXPIRADO' ? 'Expirado' : 
                        latestUpdate.data.status_novo;

      toast.success(
        `Reserva ${latestUpdate.reserva_id.slice(-8)} mudou para ${statusText}`,
        {
          description: latestUpdate.data.motivo,
          action: {
            label: "Ver detalhes",
            onClick: () => {
              setReservations(currentReservations => {
                const reserva = currentReservations.find(r => r.id === latestUpdate.reserva_id);
                if (reserva) handleViewDetails(reserva);
                return currentReservations;
              });
            }
          }
        }
      );

      // Recarregar estatísticas
      loadStatistics();
    }
  }, [reservationUpdates, loadStatistics]);

  // Processar alertas do sistema
  useEffect(() => {
    if (systemAlerts.length > 0) {
      const latestAlert = systemAlerts[0] as SocketErrorPayload | SystemUpdatePayload & { successful?: number };
      
      if (latestAlert.type === 'error' || latestAlert.type === 'system_error') {
        toast.error('Erro no sistema automático', {
          description: latestAlert.message || 'Verifique o painel de monitoramento'
        });
      } else if (latestAlert.type === 'recovery_completed' && 'successful' in latestAlert) {
        // Garante que 'successful' está presente
        toast.info('Sistema recuperado', {
          description: `${(latestAlert as SystemUpdatePayload & { successful: number }).successful} reservas processadas após período offline`
        });
      }
    }
  }, [systemAlerts]);

  // Configuração das colunas da tabela
  const { createColumn } = useTableColumns<Reserva>();
  
  const columns: ColumnDef<Reserva>[] = [
    createColumn('user', 'Cliente', (reserva) => (
      <div>
        <p className="font-medium">{reserva.user.nome_completo}</p>
        <p className="text-sm text-gray-500">{reserva.user.email}</p>
      </div>
    )),
    createColumn('data', 'Data/Hora', (reserva) => (
      <div>
        <p className="font-medium">{reservaService.formatDate(reserva.data)}</p>
        <p className="text-sm text-gray-500">{reserva.bloco.hora_inicio} - {reserva.bloco.hora_fim}</p>
      </div>
    )),
    createColumn('sala', 'Sala', (reserva) => reserva.sala.nome),
    createColumn('status', 'Status', (reserva) => (
      <div className="flex items-center gap-2">
        {getStatusIcon(reserva.status)}
        <Badge className={getStatusColor(reserva.status)}>
          {reserva.status}
        </Badge>
      </div>
    )),
    createColumn('valor', 'Valor', (reserva) => 
      reserva.valor ? `R$ ${reserva.valor.toFixed(2)}` : 'Crédito'
    ),
    createColumn('actions', 'Ações', (reserva) => (
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => handleViewDetails(reserva)}>
          <Edit className="w-4 h-4" />
        </Button>
        {(reserva.status === 'RESERVADO' || reserva.status === 'CONFIRMADO') && (
          <Button 
            size="sm" 
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={() => confirmMarcarComoUsado(reserva)}
            title="Marcar como Usado"
          >
            <CheckCircle className="w-4 h-4" />
          </Button>
        )}
        {reserva.status !== 'CANCELADO' && reserva.status !== 'USADO' && (
          <Button 
            size="sm" 
            variant="destructive" 
            onClick={() => confirmCancelReservation(reserva.id)}
            title="Cancelar Reserva"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    ))
  ];

  // Card mobile personalizado para reservas
  const renderReservationMobileCard = (reserva: Reserva, index: number) => (
    <Card key={index} className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-lg">{reserva.user.nome_completo}</h3>
        <div className="flex items-center gap-2">
          {getStatusIcon(reserva.status)}
          <Badge className={getStatusColor(reserva.status)}>
            {reserva.status}
          </Badge>
        </div>
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-gray-500" />
          <span>{reserva.user.email}</span>
        </div>
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-gray-500" />
          <span>{reservaService.formatDate(reserva.data)} - {reserva.bloco.hora_inicio} às {reserva.bloco.hora_fim}</span>
        </div>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-gray-500" />
          <span>{reserva.sala.nome}</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-gray-500" />
          <span>{reserva.valor ? `R$ ${reserva.valor.toFixed(2)}` : 'Crédito'}</span>
        </div>
      </div>
      
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => handleViewDetails(reserva)} className="flex-1">
          <Edit className="w-4 h-4 mr-2" />
          Detalhes
        </Button>
        {(reserva.status === 'RESERVADO' || reserva.status === 'CONFIRMADO') && (
          <Button 
            size="sm" 
            className="bg-green-600 hover:bg-green-700 text-white"
            onClick={() => confirmMarcarComoUsado(reserva)}
            title="Marcar como Usado"
          >
            <CheckCircle className="w-4 h-4" />
          </Button>
        )}
        {reserva.status !== 'CANCELADO' && reserva.status !== 'USADO' && (
          <Button 
            size="sm" 
            variant="destructive" 
            onClick={() => confirmCancelReservation(reserva.id)}
            title="Cancelar Reserva"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </Card>
  );

  // Métricas das reservas usando dados reais
  const metrics = [
    {
      title: "Total Reservas",
      value: statistics?.totalReservas || 0,
      change: `${statistics?.reservasAtivas || 0} ativas`,
      bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      icon: CreditCard
    },
    {
      title: "Reservas Hoje",
      value: statistics?.reservasHoje || 0,
      change: "para hoje",
      bg: "bg-gradient-to-r from-green-500 to-emerald-500",
      icon: Clock
    },
    {
      title: "Ativas",
      value: statistics?.reservasAtivas || 0,
      change: "confirmadas/reservadas",
      bg: "bg-gradient-to-r from-purple-500 to-pink-500",
      icon: CheckCircle
    },
    {
      title: "Canceladas",
      value: statistics?.reservasCanceladas || 0,
      change: "total canceladas",
      bg: "bg-gradient-to-r from-red-500 to-pink-500",
      icon: XCircle
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "CONFIRMADO": return <CheckCircle className="w-4 h-4" />;
      case "RESERVADO": return <Clock className="w-4 h-4" />;
      case "USADO": return <CheckCircle className="w-4 h-4 text-purple-600" />;
      case "CANCELADO": return <XCircle className="w-4 h-4" />;
      case "CONCLUIDO": return <CheckCircle className="w-4 h-4" />;
      case "EXPIRADO": return <AlertCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "CONFIRMADO": return "bg-green-100 text-green-800";
      case "RESERVADO": return "bg-yellow-100 text-yellow-800";
      case "USADO": return "bg-purple-100 text-purple-800";
      case "CANCELADO": return "bg-red-100 text-red-800";
      case "CONCLUIDO": return "bg-blue-100 text-blue-800";
      case "EXPIRADO": return "bg-gray-100 text-gray-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const handleChangeStatus = async (reservationId: string, newStatus: string) => {
    try {
      await reservaService.update(reservationId, { status: newStatus });
      await loadReservations(); // Recarregar dados
      toast.success('Status da reserva atualizado com sucesso!');
    } catch (error) {
      
      toast.error('Erro ao atualizar status da reserva');
    }
  };

  const handleViewDetails = (reservation: Reserva) => {
    setSelectedReservation(reservation);
    setIsDetailDialogOpen(true);
  };

  const handleCancelReservation = async (reservationId: string, motivo: string) => {
    try {
      await reservaService.cancel(reservationId, motivo);
      await loadReservations(); // Recarregar dados
      toast.success('Reserva cancelada com sucesso!');
      setShowCancelDialog(false);
      setReservaToCancel(null);
    } catch (error) {
      
      toast.error('Erro ao cancelar reserva');
    }
  };

  const confirmCancelReservation = (reservationId: string) => {
    setReservaToCancel(reservationId);
    setShowCancelDialog(true);
  };

  const handleMarcarComoUsado = async (reserva: Reserva) => {
    try {
      if (!reserva.pago_com_credito) {
        toast.error('Esta reserva não foi paga com crédito');
        return;
      }

      if (!reserva.valor_credito_usado) {
        toast.error('Reserva não possui valor de crédito definido');
        return;
      }

      await CreditoService.marcarReservaComoUsada(
        reserva.id,
        reserva.user.id,
        reserva.valor_credito_usado,
        `Horário utilizado - ${reserva.sala.nome} em ${reservaService.formatDate(reserva.data)}`
      );

      toast.success('Reserva marcada como usada com sucesso!');
      await loadReservations(); // Recarregar dados
      setShowUsadoDialog(false);
      setReservaToMarkUsado(null);
    } catch (error: unknown) {
      console.error('Erro ao marcar reserva como usada:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao marcar reserva como usada';
      toast.error(message);
    }
  };

  const confirmMarcarComoUsado = (reserva: Reserva) => {
    setReservaToMarkUsado(reserva);
    setShowUsadoDialog(true);
  };

  const filteredReservations = reservations.filter(reservation => {
    const matchesStatus = selectedStatus === "TODOS" || reservation.status === selectedStatus;
    return matchesStatus;
  });

  const statusCounts = {
    TODOS: reservations.length,
    CONFIRMADO: reservations.filter(r => r.status === "CONFIRMADO").length,
    RESERVADO: reservations.filter(r => r.status === "RESERVADO").length,
    EM_USO: reservations.filter(r => r.status === "EM_USO").length,
    USADO: reservations.filter(r => r.status === "USADO").length,
    CANCELADO: reservations.filter(r => r.status === "CANCELADO").length,
    CONCLUIDO: reservations.filter(r => r.status === "CONCLUIDO").length,
    EXPIRADO: reservations.filter(r => r.status === "EXPIRADO").length
  };

  return (
    <ResponsiveAdminLayout
      title="Gestão de Reservas"
      subtitle="Acompanhe e gerencie todas as reservas do sistema"
    >
        {/* 🆕 STATUS DE CONEXÃO E ALERTAS */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <Wifi className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-green-600">Tempo real ativo</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-red-500" />
                  <span className="text-sm text-red-600">
                    {connectionError || 'Desconectado'}
                  </span>
                </>
              )}
            </div>
            
            {connectionStats && (
              <div className="text-sm text-gray-500">
                {(connectionStats as ConnectionStats).connected_users} usuários online
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {systemAlerts.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearAlerts}
                className="text-orange-600 border-orange-200"
              >
                <Bell className="w-4 h-4 mr-2" />
                {systemAlerts.length} alertas
              </Button>
            )}
            
            {reservationUpdates.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearUpdates}
                className="text-blue-600 border-blue-200"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                {reservationUpdates.length} atualizações
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 flex flex-col md:flex-row items-center gap-3">
          <Select value={selectedClient} onValueChange={(value) => {
            setSelectedClient(value);
            setCurrentPage(1); // Resetar página ao mudar filtro
          }}>
            <SelectTrigger className="w-full md:w-[250px]">
              <SelectValue placeholder="Filtrar por Cliente" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">Todos os Clientes</SelectItem>
              {clients.map(client => (
                <SelectItem key={client.id} value={client.id}>
                  {client.nome_completo}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedStatus} onValueChange={(value) => {
            setSelectedStatus(value);
            setCurrentPage(1); // Resetar página ao mudar filtro
          }}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="TODOS">Todos ({statusCounts.TODOS})</SelectItem>
              <SelectItem value="CONFIRMADO">Confirmados ({statusCounts.CONFIRMADO})</SelectItem>
              <SelectItem value="RESERVADO">Reservados ({statusCounts.RESERVADO})</SelectItem>
              <SelectItem value="EM_USO">Em Uso ({statusCounts.EM_USO || 0})</SelectItem>
              <SelectItem value="USADO">Usados ({statusCounts.USADO || 0})</SelectItem>
              <SelectItem value="CONCLUIDO">Concluídos ({statusCounts.CONCLUIDO})</SelectItem>
              <SelectItem value="CANCELADO">Cancelados ({statusCounts.CANCELADO})</SelectItem>
              <SelectItem value="EXPIRADO">Expirados ({statusCounts.EXPIRADO})</SelectItem>
            </SelectContent>
          </Select>
        </div>

          {/* Reservations Table */}
          <Card className="bg-white shadow-sm">
            <CardContent className="p-0">
              <ResponsiveTable
                data={filteredReservations}
                columns={columns}
                renderMobileCard={renderReservationMobileCard}
                loading={loading}
                emptyMessage="Nenhuma reserva encontrada"
              />
            </CardContent>
          </Card>

          {/* Paginação */}
          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-500">
                Página {currentPage} de {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Próxima
                </Button>
              </div>
            </div>
          )}

          {/* Detail Dialog */}
          <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>Detalhes da Reserva #{selectedReservation?.id}</DialogTitle>
              </DialogHeader>
              {selectedReservation && (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Cliente</Label>
                      <p className="text-sm">{selectedReservation.user.nome_completo}</p>
                      <p className="text-xs text-gray-500">{selectedReservation.user.email}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Status</Label>
                      <Badge className={reservaService.getStatusColor(selectedReservation.status)}>
                        {reservaService.formatStatus(selectedReservation.status)}
                      </Badge>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Sala</Label>
                      <p className="text-sm">{selectedReservation.sala.nome}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Data</Label>
                      <p className="text-sm">{reservaService.formatDate(selectedReservation.data)}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Horário</Label>
                      <p className="text-sm">{selectedReservation.bloco.hora_inicio} - {selectedReservation.bloco.hora_fim}</p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Valor</Label>
                      <p className="text-sm">{reservaService.formatCurrency(selectedReservation.valor)}</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Forma de Pagamento</Label>
                      <p className="text-sm">{selectedReservation.pago_com_credito ? "Crédito" : "Stripe"}</p>
                      {selectedReservation.pago_com_credito && selectedReservation.valor_credito_usado && (
                        <p className="text-xs text-gray-500">{reservaService.formatCurrency(selectedReservation.valor_credito_usado)} usado</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-600">Criado em</Label>
                      <p className="text-sm">{reservaService.formatDateTime(selectedReservation.created_at)}</p>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm font-medium text-gray-600">Última Atualização</Label>
                    <p className="text-sm">{reservaService.formatDateTime(selectedReservation.updated_at)}</p>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Modal de Confirmação - Cancelar Reserva */}
          <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle>Confirmar Cancelamento</DialogTitle>
                <DialogDescription>
                  Tem certeza que deseja cancelar esta reserva? Esta ação não pode ser desfeita.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCancelDialog(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => reservaToCancel && handleCancelReservation(reservaToCancel, 'Cancelado pelo admin')}
                >
                  Confirmar Cancelamento
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Modal de Confirmação - Marcar como Usado */}
          <Dialog open={showUsadoDialog} onOpenChange={setShowUsadoDialog}>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle>Confirmar Uso da Reserva</DialogTitle>
                <DialogDescription>
                  Tem certeza que deseja marcar esta reserva como usada? 
                  {reservaToMarkUsado && !reservaToMarkUsado.pago_com_credito && (
                    <span className="block mt-2 font-medium text-yellow-600">
                      ⚠️ Esta reserva não foi paga com crédito. A marcação como "usado" será apenas informativa.
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowUsadoDialog(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700" 
                  onClick={() => reservaToMarkUsado && handleMarcarComoUsado(reservaToMarkUsado)}
                >
                  Confirmar Uso
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
    </ResponsiveAdminLayout>
  );
};

export default AdminReservations; 
